// Hap data + playhead access.
//
// Perf model (changed 2026-04):
//
//   Previously, useHapData set React state (setFrame) at ~12fps. That
//   forced the component owning this hook — App — to re-render 12
//   times per second during playback, which then propagated via props
//   into every child. Even with React.memo everywhere, the parent's
//   JSX had to evaluate and memo comparators had to run. For a tree
//   with 10+ channels and a VDOM that reaches into CodeMirror, it was
//   the single largest cause of perceived lag.
//
//   Now, useHapData writes everything except `voiceOrder` +
//   `patternEpoch` to refs. The polling loop runs on the shared rAF
//   ticker (see ./sharedTicker.ts) and only calls setState when the
//   voice order or pattern identity changes — i.e., when the set of
//   lanes the PerformanceStrip should render has changed. That's rare (only
//   on re-evaluate). Everything else — live cycle count, tempo, hap
//   stream — is a ref read.
//
//   Downstream consumers that need fast data subscribe to the ticker
//   and read the refs. VoiceTrace, CyclePhasor, LevelMeter, and the
//   TransportDeck readouts all do this. Reconciliation during playback
//   drops to zero above the voice-lane level.

import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { getRepl } from './player';
import { subscribeTick } from './sharedTicker';
import type { Hap, HapValue, Pattern } from './types';

export type VisualHap = {
  key: string;
  begin: number;
  end: number;
  duration: number;
  voice: string;
  value: HapValue;
};

/** Live refs for fast-changing data. Never null — the hook seeds each
 *  ref with a safe initial value so consumers can read before the
 *  first poll resolves. */
export type HapRefs = {
  /** All currently-queried haps in the lookbehind + lookahead window. */
  haps: MutableRefObject<VisualHap[]>;
  /** Bumps on every assignment to `haps.current`. Consumers that want
   *  to react only to *content* changes (not every tick) can skip
   *  work when this hasn't advanced since their last read. */
  hapsVersion: MutableRefObject<number>;
  /** Floor of scheduler.now() at the most recent poll. */
  cycle: MutableRefObject<number>;
  /** Current cycles-per-second of the scheduler. */
  cps: MutableRefObject<number>;
  /** Snapshot of scheduler.now() at the most recent poll. */
  pollNow: MutableRefObject<number>;
  /** Whether the scheduler is actively running. */
  started: MutableRefObject<boolean>;
};

/** Subset of frame data that drives structural renders — the list of
 *  voices is what the PerformanceStrip uses to lay out its lanes. */
export type HapStructuralFrame = {
  voiceOrder: readonly string[];
  /** Bumps on every pattern re-evaluation; useful as a stable key. */
  patternEpoch: number;
};

export type HapDataResult = {
  refs: HapRefs;
  structural: HapStructuralFrame;
};

const MAX_TRACKED_VOICES = 16;

// Backward-compat shape retained for callers that still consume the
// combined frame object. New callers should prefer the refs+structural
// shape returned by useHapData.
export type HapDataFrame = {
  haps: VisualHap[];
  voiceOrder: readonly string[];
  patternEpoch: number;
  started: boolean;
  cps: number;
  cycle: number;
  pollNow: number;
};

type HapDataOptions = {
  queryBehind?: number;
  queryAhead?: number;
  /** Target polling rate for the internal tick. The shared ticker
   *  fires at display refresh, so we throttle here — 12fps is plenty
   *  for hap data because the visual elements tied to it (flares,
   *  level meters) interpolate motion from the playhead ref at 60fps. */
  fps?: number;
};

export function useHapData({
  queryBehind = 2,
  queryAhead = 4,
  fps = 12,
}: HapDataOptions = {}): HapDataResult {
  const hapsRef = useRef<VisualHap[]>([]);
  const hapsVersionRef = useRef(0);
  const cycleRef = useRef(0);
  const cpsRef = useRef(0.5);
  const pollNowRef = useRef(0);
  const startedRef = useRef(false);

  const [structural, setStructural] = useState<HapStructuralFrame>(() => ({
    voiceOrder: [],
    patternEpoch: 0,
  }));

  const voiceOrderRef = useRef<string[]>([]);
  const patternEpochRef = useRef(0);
  const prevPatternRef = useRef<Pattern | null>(null);
  const lastEmitRef = useRef(0);

  useEffect(() => {
    const minInterval = 1000 / fps;
    return subscribeTick((nowMs) => {
      const repl = getRepl();
      const scheduler = repl?.scheduler;
      if (!repl || !scheduler) return;

      // Pattern-identity check — a new reference means re-evaluate.
      const pattern = scheduler.pattern;
      const patternChanged = pattern !== prevPatternRef.current;
      if (patternChanged) {
        prevPatternRef.current = pattern ?? null;
        patternEpochRef.current++;
        voiceOrderRef.current = [];
      }

      // Throttle the heavy work (queryArc + hap normalization) even
      // though we run on the shared display-rate ticker. The ref-
      // based fast values are ALSO throttled here — they don't need
      // 60fps refresh because motion is driven by the separate
      // nowRef tick, which runs every frame.
      if (!patternChanged && nowMs - lastEmitRef.current < minInterval) return;
      lastEmitRef.current = nowMs;

      // Fast-path ref updates even when there's no pattern yet, so
      // readouts (BPM = 0, started = false) reflect reality.
      startedRef.current = scheduler.started;
      cpsRef.current = scheduler.cps;

      if (!pattern) {
        hapsRef.current = [];
        hapsVersionRef.current++;
        cycleRef.current = 0;
        pollNowRef.current = 0;
        if (patternChanged) {
          setStructural({
            voiceOrder: [],
            patternEpoch: patternEpochRef.current,
          });
        }
        return;
      }

      const now = scheduler.now();
      const begin = now - queryBehind;
      const end = now + queryAhead;

      let raw: Hap[] = [];
      try {
        raw = pattern.queryArc(begin, end);
      } catch {
        // Evaluate can transiently leave the pattern unqueryable.
        return;
      }

      const haps: VisualHap[] = [];
      const order = voiceOrderRef.current;
      const orderChanged = { val: patternChanged }; // tracks via closure
      for (const hap of raw) {
        if (!hap.hasOnset()) continue;
        if (!hap.whole) continue;
        const b = hap.whole.begin.valueOf();
        const e = hap.whole.end.valueOf();
        const value = normaliseValue(hap.value);
        const voice = voiceOf(value);
        haps.push({
          key: `${b.toFixed(5)}:${voice}:${labelOf(value)}`,
          begin: b,
          end: e,
          duration: e - b,
          voice,
          value,
        });
        if (!order.includes(voice) && order.length < MAX_TRACKED_VOICES) {
          order.push(voice);
          orderChanged.val = true;
        }
      }

      hapsRef.current = haps;
      hapsVersionRef.current++;
      cycleRef.current = Math.floor(now);
      pollNowRef.current = now;

      // React renders only when structure changes. In steady-state
      // playback of a stable pattern, this branch is never taken —
      // App / PerformanceStrip / everything downstream stays idle.
      if (orderChanged.val) {
        setStructural({
          voiceOrder: order.slice(),
          patternEpoch: patternEpochRef.current,
        });
      }
    });
  }, [queryBehind, queryAhead, fps]);

  return {
    refs: {
      haps: hapsRef,
      hapsVersion: hapsVersionRef,
      cycle: cycleRef,
      cps: cpsRef,
      pollNow: pollNowRef,
      started: startedRef,
    },
    structural,
  };
}

/**
 * Filter the global hap stream to just the haps for one voice, held
 * as React state that updates only when that voice's hap set changes
 * in content (not on every tick). Used by VoiceTrace to render its
 * ticks/flares without re-rendering when haps are content-stable.
 */
export function useVoiceHaps(
  refs: HapRefs,
  voice: string,
): VisualHap[] {
  const [haps, setHaps] = useState<VisualHap[]>([]);
  const lastVersionRef = useRef(-1);
  const lastKeysRef = useRef<string>('');

  useEffect(() => {
    // Reset on voice change so a newly-mounted lane pulls data
    // even if the global version hasn't advanced.
    lastVersionRef.current = -1;
    lastKeysRef.current = '';
    return subscribeTick(() => {
      const version = refs.hapsVersion.current;
      if (version === lastVersionRef.current) return;
      lastVersionRef.current = version;

      const all = refs.haps.current;
      const mine: VisualHap[] = [];
      let keys = '';
      for (const h of all) {
        if (h.voice !== voice) continue;
        mine.push(h);
        keys += h.key + '|';
      }
      if (keys === lastKeysRef.current) return;
      lastKeysRef.current = keys;
      setHaps(mine);
    });
  }, [refs, voice]);

  return haps;
}

/**
 * Shared now-ref: current playhead position in cycles, updated every
 * display frame from the scheduler. Visual consumers read this in
 * their own rAF (via the shared ticker) to drive motion without
 * triggering React renders.
 *
 * `offsetMs` shifts the reported time backwards — a calibration knob
 * for audio output latency.
 */
export function useNowRef(offsetMs: number): MutableRefObject<number> {
  const nowRef = useRef(0);
  const offsetRef = useRef(offsetMs);
  offsetRef.current = offsetMs;

  useEffect(() => {
    return subscribeTick(() => {
      const repl = getRepl();
      const sched = repl?.scheduler;
      if (sched?.started) {
        const n = sched.now();
        const shift = (offsetRef.current / 1000) * sched.cps;
        nowRef.current = n - shift;
      }
      // When stopped, freeze the ref wherever it was — visualisers
      // stay where they stopped rather than jumping to 0.
    });
  }, []);

  return nowRef;
}

/**
 * Helper for components (TransportDeck's play indicator, the
 * PerformanceStrip's "transmitting" LED) that need to react to engine
 * start/stop events without subscribing to the full hap stream.
 * Returns React state that flips only when startedRef changes.
 *
 * Takes the ref as an argument rather than calling useHapData —
 * useHapData should be called exactly once in the tree (App owns it)
 * so a single polling loop serves all consumers.
 */
export function useStartedState(
  startedRef: MutableRefObject<boolean>,
): boolean {
  const [started, setStarted] = useState(startedRef.current);
  useEffect(() => {
    let last = startedRef.current;
    return subscribeTick(() => {
      const now = startedRef.current;
      if (now !== last) {
        last = now;
        setStarted(now);
      }
    });
  }, [startedRef]);
  return started;
}

function normaliseValue(v: Hap['value']): HapValue {
  if (typeof v === 'string') return { s: v };
  if (typeof v === 'number') return { note: v };
  return v;
}

export function voiceOf(value: HapValue): string {
  if (typeof value.s === 'string') return value.s;
  if (value.note !== undefined) return 'note';
  return 'ctrl';
}

export function labelOf(value: HapValue): string {
  if (value.note !== undefined) return String(value.note);
  if (typeof value.s === 'string') return value.s;
  return '·';
}
